import React, { useState, useEffect, useRef, useCallback } from "react";
import * as echarts from "echarts";
import moment from "moment";
import ReactDatePicker from "react-datepicker";
import Loader from "react-spinner-loader";
import { FullScreen, useFullScreenHandle } from "react-full-screen";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faInfoCircle, faCity } from "@fortawesome/free-solid-svg-icons";
import { Expand } from "../../../../Components/IconComponents";
import LMSButton from "../../Common/LMSButton/LMSButton";
import { getData, postDataWithBody } from "../../../../Services/Services";
import { heatwavecardsdata, heatwavechartdata, heatwavepopupdata } from "../../../../Services/EndPoints";
import "react-datepicker/dist/react-datepicker.css";
import "./HeatwaveDashboard.css";

// ── Blue/Teal palette (matching Avalanche) ────────────────────────
const C = {
  bg: "#F0F6FB",
  surface: "#FFFFFF",
  navBg: "#1C4E72",
  primary: "#2A6591",
  text1: "#0F2D42",
  text2: "#3A5F78",
  text3: "#7A9BB0",
  border: "rgba(42,101,145,.10)",
};

// ── Pastel icon palette (Standard mixed) ──────────────────
const PALETTE = [
  { bg: "#FFF0F0", color: "#D95555" },
  { bg: "#FFF4EA", color: "#D4772A" },
  { bg: "#EEFAF3", color: "#27A05A" },
  { bg: "#EBF4FF", color: "#3579D4" },
];

// ── Card Config ───────────────────────────────────────────────────────────
const dashboardCardConfigForHeatwave = {
  data: [
    { key: "no_of_deaths_today", label: "Deaths" },
    { key: "no_of_persons_hospitalized_today", label: "Hospitalized Today" },
    { key: "no_of_persons_hospitalized_pay_done_today", label: "Hospitalized Payments" },
    { key: "no_of_dec_ex_gratia_pay_done_today", label: "Ex-Gratia Payments" },
  ],
};

const formatLabel = (key) => ({
  no_of_deaths_today: "Deaths",
  no_of_persons_hospitalized_today: "Hospitalized",
  no_of_persons_hospitalized_pay_done_today: "Medical Payments",
  no_of_dec_ex_gratia_pay_done_today: "Ex-Gratia Payments",
}[key] || key);

// ── SVG Icons (matching cold wave icon style) ────────────────────────────
function HeatWaveIcon({ keyName = "", label = "", color = "#2A6591" }) {
  const k = (keyName + " " + label).toLowerCase();
  const sw = "1.55";
  const r = "round";

  if (k.includes("death") || k.includes("loss") || k.includes("fatal"))
    return (
      <svg viewBox="0 0 20 20" fill="none" width="17" height="17">
        <circle cx="10" cy="6.5" r="2.8" stroke={color} strokeWidth={sw}/>
        <path d="M4.5 18c0-3.038 2.462-5.5 5.5-5.5s5.5 2.462 5.5 5.5" stroke={color} strokeWidth={sw} strokeLinecap={r}/>
        <path d="M7.8 5l4.4 4.4M12.2 5l-4.4 4.4" stroke={color} strokeWidth="1.4" strokeLinecap={r}/>
      </svg>
    );

  if (k.includes("hospital") && !k.includes("pay"))
    return (
      <svg viewBox="0 0 20 20" fill="none" width="17" height="17">
        <rect x="3" y="3" width="14" height="14" rx="4" stroke={color} strokeWidth={sw}/>
        <path d="M10 7v6M7 10h6" stroke={color} strokeWidth="1.8" strokeLinecap={r}/>
      </svg>
    );

  if (k.includes("hospital") && k.includes("pay"))
    return (
      <svg viewBox="0 0 20 20" fill="none" width="17" height="17">
        <rect x="3" y="3" width="14" height="14" rx="4" stroke={color} strokeWidth={sw}/>
        <path d="M10 7v6M7 10h6" stroke={color} strokeWidth="1.8" strokeLinecap={r}/>
      </svg>
    );

  if (k.includes("gratia") || k.includes("compens"))
    return (
      <svg viewBox="0 0 20 20" fill="none" width="17" height="17">
        <path d="M10 2.5L3.5 5v5.5c0 4 2.9 6.5 6.5 7 3.6-.5 6.5-3 6.5-7V5L10 2.5z"
          stroke={color} strokeWidth={sw} strokeLinejoin={r}/>
        <path d="M7.5 10l2 2 3-3.5" stroke={color} strokeWidth="1.5" strokeLinecap={r} strokeLinejoin={r}/>
      </svg>
    );

  return (
    <svg viewBox="0 0 20 20" fill="none" width="17" height="17">
      <path d="M2 16l5.5-10 3 5 2-3L17 16H2z" stroke={color} strokeWidth={sw} strokeLinejoin={r}/>
      <circle cx="14" cy="5" r="2" stroke={color} strokeWidth="1.3"/>
    </svg>
  );
}

// ── Popup ──────────────────────────────────────────────────────────────────
const DistrictPopup = ({ show, onHide, districtData, incidentType, blocks }) => {
  if (!show) return null;

  return (
    <div className="hwd-popup-overlay" onClick={onHide}>
      <div className="hwd-popup" onClick={e => e.stopPropagation()}>
        <div className="hwd-popup-header">
          <div className="hwd-popup-header-left">
            <div className="hwd-popup-logo">
              <svg viewBox="0 0 24 24" fill="currentColor" width="16" height="16">
                <path d="M7.692 14.038 2.009 17.39l-1.006-1.729 3.943-2.295a6.5 6.5 0 0 1-.363-1.367H0v-2h4.585q.111-.715.369-1.371L.998 6.326l1.006-1.729 3.957 2.304q.432-.528.964-.958L4.627 1.992 6.356.986l2.302 3.958a6.5 6.5 0 0 1 1.343-.359V0h2v4.58c.468.072.924.192 1.359.362L15.659.987l1.729 1.005-2.299 3.953q.524.427.957.961l3.966-2.309 1.006 1.729-3.968 2.31c.117.299.216.604.287.916a7.5 7.5 0 0 0-2.295-.532A4.5 4.5 0 0 0 6.5 11a4.42 4.42 0 0 0 1.192 3.038m11.717-.022a5.51 5.51 0 0 0-4.909-3.017 5.507 5.507 0 0 0-5.487 5.123A4.01 4.01 0 0 0 6 19.999c0 2.206 1.794 4 4 4h1.008a5 5 0 0 1-.902-2 2.01 2.01 0 0 1-2.105-2c0-1.103.897-2 2.027-2h1.113c-.026-.298-.147-1.212-.141-1.5 0-1.93 1.57-3.5 3.5-3.5 1.481 0 2.808.938 3.302 2.333l.235.667H19c1.654 0 3 1.346 3 3a3.005 3.005 0 0 1-2.082 2.855 4.95 4.95 0 0 1-.926 2.145H19c2.757 0 5-2.243 5-5a5.006 5.006 0 0 0-4.591-4.983m-2.296 4.854a3.005 3.005 0 0 1 .008 4.25c-.566.566-1.32.879-2.121.879s-1.555-.312-2.121-.879a3 3 0 0 1 0-4.242L15 16.802zm-1.406 1.422L15 19.601l-.715.699a1 1 0 0 0 .008 1.406 1.025 1.025 0 0 0 1.414 0 1 1 0 0 0 0-1.414" 
                  fill="white"/>
              </svg>
            </div>
            <div style={{ minWidth: 0 }}>
              <div className="hwd-popup-overline">HeatWave Impact Zone</div>
              <div className="hwd-popup-title-text">{districtData} • {formatLabel(incidentType)}</div>
            </div>
          </div>
          <button className="hwd-popup-close" onClick={onHide}>✕</button>
        </div>

        <div className="hwd-popup-body">
          {blocks?.length > 0 ? (
            blocks.map((block, i) => (
              <div key={i} className="hwd-popup-row">
                <div className="hwd-popup-row-left">
                  <div className="hwd-popup-row-num">{i + 1}</div>
                  <FontAwesomeIcon icon={faCity} style={{ color: C.primary, fontSize: 12 }} />
                  <span>{block.block_name}</span>
                </div>
                <div className="hwd-popup-row-badge">
                  {block.count.toLocaleString("en-IN")}
                </div>
              </div>
            ))
          ) : (
            <div className="hwd-popup-empty">
              <FontAwesomeIcon icon={faInfoCircle} className="hwd-popup-empty-icon" />
              <div className="hwd-popup-empty-text">No Block Data Available</div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ── Main Dashboard ─────────────────────────────────────────────────────────
export default function HeatWaveDashboards() {
  const handle = useFullScreenHandle();
  const [loader, setLoader] = useState(false);
  const [error, setError] = useState(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [cardData, setCardData] = useState({});
  const [selectedIncident, setSelectedIncident] = useState("");
  const [popupState, setPopupState] = useState({ show: false, district: null, blocks: [], incidentType: null });
  const [isTrendExpanded, setIsTrendExpanded] = useState(false);
  const [isTrendMinimized, setIsTrendMinimized] = useState(false);
  const [selectedYear, setSelectedYear] = useState(2026);

  // ✅ FIX: Match original — from Jan 1 current year to today
  const [internalFromDate, setInternalFromDate] = useState(new Date(new Date().getFullYear(), 0, 1));
  const [internalToDate, setInternalToDate] = useState(new Date());
  const [appliedFromDate, setAppliedFromDate] = useState(new Date(new Date().getFullYear(), 0, 1));
  const [appliedToDate, setAppliedToDate] = useState(new Date());

  // ✅ FIX: Derive date strings from applied dates (safe against null)
  const startDateStr = appliedFromDate ? moment(appliedFromDate).format("YYYY-MM-DD") : "";
  const endDateStr = appliedToDate ? moment(appliedToDate).format("YYYY-MM-DD") : "";

  // ✅ FIX: fetchCardData reads the passed params, not stale state
  const fetchCardData = useCallback(async (fromDate, toDate) => {
    const s = moment(fromDate).format("YYYY-MM-DD");
    const e = moment(toDate).format("YYYY-MM-DD");
    setLoader(true);
    setError(null);
    try {
      const data = await getData(heatwavecardsdata.replace("FROMDATE", s).replace("TODATE", e));
      setCardData(data?.[0] || {});
    } catch {
      setError("Failed to load data.");
    } finally {
      setLoader(false);
    }
  }, []);

  // ✅ FIX: Fetch on initial mount with correct dates
  useEffect(() => {
    fetchCardData(appliedFromDate, appliedToDate);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ✅ FIX: Apply button — set dates, then fetch with the NEW values directly
  const handleApply = () => {
    if (!internalFromDate || !internalToDate) {
      setError("Please select both From and To dates");
      return;
    }
    if (internalFromDate > internalToDate) {
      setError("From date cannot be greater than To date");
      return;
    }
    setError(null);
    setAppliedFromDate(internalFromDate);
    setAppliedToDate(internalToDate);
    // ✅ Pass the new values directly to avoid stale closure
    fetchCardData(internalFromDate, internalToDate);
  };

  const handleYearChange = (e) => {
    const year = parseInt(e.target.value);
    setSelectedYear(year);
    const today = new Date();
    const newFrom = new Date(year, 0, 1);
    const yearEnd = new Date(year, 11, 31);
    const nextTo = today <= yearEnd ? today : yearEnd;
    setInternalFromDate(newFrom);
    setInternalToDate(nextTo);
    setAppliedFromDate(newFrom);
    setAppliedToDate(nextTo);
    fetchCardData(newFrom, nextTo);
  };

  // ✅ FIX: Validate dates safely (handle null from isClearable)
  useEffect(() => {
    if (internalFromDate && internalToDate && internalFromDate > internalToDate) {
      setError("Select proper From Date and To Date");
    } else if (!internalFromDate || !internalToDate) {
      setError(null); // Don't show error when cleared, just disable Apply
    } else {
      setError(null);
    }
  }, [internalFromDate, internalToDate]);

  const handleBarClick = async (district) => {
    try {
      const regionIds = district.region_id.split(",").map(Number);
      const blockData = await postDataWithBody(heatwavepopupdata, {
        region_id_list: regionIds,
        from_date: appliedFromDate,
        to_date: appliedToDate,
        bar_type: selectedIncident,
      }, {});
      setPopupState({ show: true, district: district.dist_name, blocks: blockData || [], incidentType: selectedIncident });
    } catch {
      setPopupState({ show: true, district: district.dist_name, blocks: [], incidentType: selectedIncident });
    }
  };

  const toggleFullscreen = () => {
    isFullscreen ? handle.exit() : handle.enter();
    setIsFullscreen(!isFullscreen);
  };

  // ✅ FIX: Disable Apply when dates are invalid or missing
  const isApplyDisabled = !!error || !internalFromDate || !internalToDate;

  return (
    <FullScreen handle={handle}>
      <div className={`hwd-root${isFullscreen ? " hwd-fs" : ""}`}>
        <Loader show={loader} type="body" stack="vertical" message="Loading Data" />

        {/* ── NAV ── */}
        <nav className="hwd-nav">
          <div className="hwd-nav-brand">
            <div className="hwd-nav-logo">
              <svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20">
                <path d="M7.692 14.038 2.009 17.39l-1.006-1.729 3.943-2.295a6.5 6.5 0 0 1-.363-1.367H0v-2h4.585q.111-.715.369-1.371L.998 6.326l1.006-1.729 3.957 2.304q.432-.528.964-.958L4.627 1.992 6.356.986l2.302 3.958a6.5 6.5 0 0 1 1.343-.359V0h2v4.58c.468.072.924.192 1.359.362L15.659.987l1.729 1.005-2.299 3.953q.524.427.957.961l3.966-2.309 1.006 1.729-3.968 2.31c.117.299.216.604.287.916a7.5 7.5 0 0 0-2.295-.532A4.5 4.5 0 0 0 6.5 11a4.42 4.42 0 0 0 1.192 3.038m11.717-.022a5.51 5.51 0 0 0-4.909-3.017 5.507 5.507 0 0 0-5.487 5.123A4.01 4.01 0 0 0 6 19.999c0 2.206 1.794 4 4 4h1.008a5 5 0 0 1-.902-2 2.01 2.01 0 0 1-2.105-2c0-1.103.897-2 2.027-2h1.113c-.026-.298-.147-1.212-.141-1.5 0-1.93 1.57-3.5 3.5-3.5 1.481 0 2.808.938 3.302 2.333l.235.667H19c1.654 0 3 1.346 3 3a3.005 3.005 0 0 1-2.082 2.855 4.95 4.95 0 0 1-.926 2.145H19c2.757 0 5-2.243 5-5a5.006 5.006 0 0 0-4.591-4.983m-2.296 4.854a3.005 3.005 0 0 1 .008 4.25c-.566.566-1.32.879-2.121.879s-1.555-.312-2.121-.879a3 3 0 0 1 0-4.242L15 16.802zm-1.406 1.422L15 19.601l-.715.699a1 1 0 0 0 .008 1.406 1.025 1.025 0 0 0 1.414 0 1 1 0 0 0 0-1.414" 
                  fill="white" opacity=".9"/>
              </svg>
            </div>
            <div className="hwd-nav-title">HeatWave Dashboard</div>
          </div>

          <div className="hwd-nav-right">
            <div className="hwd-nav-group">
              <span className="hwd-nav-label">Year</span>
              <select className="hwd-nav-select" value={selectedYear} onChange={handleYearChange}>
                <option value={2024}>2024</option>
                <option value={2025}>2025</option>
                <option value={2026}>2026</option>
              </select>
            </div>

            {/* Date range pill with ReactDatePicker (same as cold wave) */}
            <div className="hwd-daterange">
              <div className="hwd-daterange-field">
                <span className="hwd-dr-label">FROM</span>
                <ReactDatePicker
                  selected={internalFromDate}
                  onChange={(date) => setInternalFromDate(date)}
                  dateFormat="dd/MM/yyyy"
                  isClearable
                  placeholderText="Select date"
                  todayButton="Today"
                  className="hwd-dp-input"
                  clearButtonClassName="hwd-dp-clear"
                />
              </div>
              <div className="hwd-dr-divider" />
              <div className="hwd-daterange-field">
                <span className="hwd-dr-label">TO</span>
                <ReactDatePicker
                  selected={internalToDate}
                  onChange={(date) => setInternalToDate(date)}
                  dateFormat="dd/MM/yyyy"
                  isClearable
                  placeholderText="Select date"
                  todayButton="Today"
                  className="hwd-dp-input"
                  clearButtonClassName="hwd-dp-clear"
                />
              </div>
            </div>

            <button className="hwd-btn-apply" onClick={handleApply} disabled={isApplyDisabled}>
              Apply
            </button>

            <button className="hwd-btn-icon" onClick={toggleFullscreen}>
              {isFullscreen ? (
                <svg viewBox="0 0 18 18" fill="none" width="14" height="14">
                  <path d="M2 6H6V2M16 6H12V2M2 12H6V16M16 12H12V16" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/>
                </svg>
              ) : (
                <svg viewBox="0 0 18 18" fill="none" width="14" height="14">
                  <path d="M2 6V2h4M12 2h4v4M2 12v4h4M16 12v4h-4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/>
                </svg>
              )}
            </button>
          </div>
        </nav>

        {error && (
          <div className="hwd-error-strip">
            <svg viewBox="0 0 16 16" fill="none" width="13" height="13">
              <circle cx="8" cy="8" r="6.5" stroke="currentColor" strokeWidth="1.4"/>
              <path d="M8 5v3.5M8 10.5v.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
            </svg>
            {error}
          </div>
        )}

        <div className="hwd-content">

          {/* ── HORIZONTAL CARDS ROW (top) ── */}
          <div className="hwd-cards-row">
            <div className="hwd-cards-inner">
              {dashboardCardConfigForHeatwave.data.map((cfg, idx) => {
                const p = PALETTE[idx % PALETTE.length];
                const active = selectedIncident === cfg.key;
                return (
                  <div
                    key={cfg.key}
                    className={`hwd-card-item${active ? " hwd-card-item--active" : ""}`}
                    onClick={() => setSelectedIncident(cfg.key)}
                  >
                    <span className="hwd-card-icon" style={{ background: p.bg }}>
                      <HeatWaveIcon keyName={cfg.key} label={cfg.label} color={p.color} />
                    </span>
                    <span className="hwd-card-text">
                      <span className="hwd-card-label">{cfg.label}</span>
                      <span className="hwd-card-value">
                        {cardData[cfg.key] != null ? cardData[cfg.key].toLocaleString("en-IN") : "0"}
                      </span>
                    </span>
                  </div>
                );
              })}
            </div>
          </div>

          {/* ── CHART AREA (below cards, full width) ── */}
          <div className={`hwd-chart-wrap${isTrendExpanded ? " hwd-chart-wrap--expanded" : ""}`}>
            <div className="hwd-chart-top">
              <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                <div>
                  <h3 className="hwd-chart-heading">
                    {selectedIncident ? formatLabel(selectedIncident) : "Incident Trend Overview"}
                  </h3>
                  {!selectedIncident && (
                    <p className="hwd-chart-range">Select any incident from the cards above to view its trend</p>
                  )}
                  {selectedIncident && (
                    <p className="hwd-chart-range">
                      {moment(startDateStr).format("DD/MM/YYYY")} — {moment(endDateStr).format("DD/MM/YYYY")}
                    </p>
                  )}
                </div>
              </div>
              
              <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                {selectedIncident && (
                  <button className="hwd-badge-btn" onClick={() => setSelectedIncident("")}>
                    {formatLabel(selectedIncident)} ✕
                  </button>
                )}
                
                {!isTrendExpanded && (
                  <button 
                    className="hwd-btn-icon" 
                    style={{ background: "rgba(42,101,145,.08)", border: "1px solid rgba(42,101,145,.12)", color: "var(--hw-primary)" }}
                    onClick={() => setIsTrendMinimized(!isTrendMinimized)}
                    title={isTrendMinimized ? "Expand Card" : "Collapse Card"}
                  >
                    {isTrendMinimized ? (
                      <svg viewBox="0 0 16 16" fill="none" width="12" height="12"><path d="M4 8h8M8 4v8" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/></svg>
                    ) : (
                      <svg viewBox="0 0 16 16" fill="none" width="12" height="12"><path d="M4 8h8" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/></svg>
                    )}
                  </button>
                )}

                <button 
                  className="hwd-btn-icon" 
                  style={{ background: "rgba(42,101,145,.08)", border: "1px solid rgba(42,101,145,.12)", color: "var(--hw-primary)" }}
                  onClick={() => setIsTrendExpanded(!isTrendExpanded)}
                  title={isTrendExpanded ? "Collapse Fullscreen" : "Expand Fullscreen"}
                >
                  {isTrendExpanded ? (
                    <svg viewBox="0 0 16 16" fill="none" width="12" height="12"><path d="M13 7H9V3M9 7L15 1M7 13V9H3M7 9L1 15" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/></svg>
                  ) : (
                    <svg viewBox="0 0 16 16" fill="none" width="12" height="12"><path d="M10 1H15V6M15 1L9 7M6 15H1V10M1 15L7 9" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/></svg>
                  )}
                </button>
              </div>
            </div>

            {(isTrendExpanded || !isTrendMinimized) && (
              <>
                {/* Empty state */}
                {!selectedIncident && (
                  <div className="hwd-chart-empty">
                    <div className="hwd-chart-empty-icon">
                      <svg viewBox="0 0 48 48" fill="none" width="40" height="40">
                        <rect x="4" y="30" width="8" height="14" rx="2" fill="#C5DFF0"/>
                        <rect x="16" y="20" width="8" height="24" rx="2" fill="#A0C8E0"/>
                        <rect x="28" y="12" width="8" height="32" rx="2" fill="#7AB4D0"/>
                        <rect x="40" y="22" width="8" height="22" rx="2" fill="#5AA0C0"/>
                        <path d="M4 8h40" stroke="#C5DFF0" strokeWidth="1.5" strokeDasharray="3 3"/>
                      </svg>
                    </div>
                    <p className="hwd-chart-empty-text">Click any incident card above to visualize its trend</p>
                  </div>
                )}

                <div className="hwd-chart-area">
                  <HeatWaveBarChart
                    selectedIncident={selectedIncident}
                    startDateStr={startDateStr}
                    endDateStr={endDateStr}
                    onBarClick={handleBarClick}
                    isTrendExpanded={isTrendExpanded}
                  />
                </div>
              </>
            )}
          </div>

          {/* ── Bottom charts ── */}
          <div className="hwd-bottom-charts">
            <HeatwavePieChart cardData={cardData} />
            <HeatwaveLineChart cardData={cardData} />
            <HeatwaveFunnelChart cardData={cardData} />
          </div>

          {/* ── Popup ── */}
          <DistrictPopup
            show={popupState.show}
            onHide={() => setPopupState({ show: false, district: null, blocks: [], incidentType: null })}
            districtData={popupState.district}
            incidentType={popupState.incidentType}
            blocks={popupState.blocks}
          />
        </div>
      </div>
    </FullScreen>
  );
}

// ── Bar Chart ──────────────────────────────────────────────────────────────
function HeatWaveBarChart({ selectedIncident, startDateStr, endDateStr, onBarClick, isTrendExpanded }) {
  const chartRef = useRef(null);
  const chartInstance = useRef(null);
  const [chartData, setChartData] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!selectedIncident || !startDateStr || !endDateStr) return;
    setLoading(true);
    getData(
      heatwavechartdata
        .replace("FROMDATE", startDateStr)
        .replace("TODATE", endDateStr)
        .replace("BARTYPE", selectedIncident)
    )
      .then(r => setChartData(r || []))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [selectedIncident, startDateStr, endDateStr]);

  // Dynamic height: 45px per bar, minimum 150px, max 500px
  const validBars = chartData.filter(i => i.count > 0).length;
  const dynamicHeight = isTrendExpanded ? "calc(100vh - 160px)" : Math.max(150, Math.min(500, validBars * 45 + 80));

  useEffect(() => {
    if (!chartRef.current || !chartData.length) return;
    const sorted = [...chartData.filter(i => i.count > 0)].sort((a, b) => a.count - b.count);
    if (!sorted.length) return;

    const option = {
      tooltip: {
        trigger: "axis", axisPointer: { type: "shadow" },
        formatter: p => `<div style="font-weight:700;color:${C.text1};border-bottom:1px solid ${C.border};margin-bottom:6px;padding-bottom:4px">${p[0].name}</div><span style="color:${C.text3}">Count: </span><span style="font-weight:700;color:${C.primary}">${p[0].value.toLocaleString("en-IN")}</span>`,
        backgroundColor: C.surface, borderColor: C.border, borderWidth: 1,
        extraCssText: "border-radius:12px;box-shadow:0 4px 16px rgba(15,45,66,.1);font-family:'Inter',sans-serif;",
      },
      grid: { left: "15%", right: "10%", bottom: 36, top: 20, containLabel: false },
      xAxis: {
        type: "value",
        axisLabel: { color: C.text3, fontSize: 11 },
        splitLine: { lineStyle: { color: C.border, type: "dashed" } },
        axisTick: { show: false },
      },
      yAxis: {
        type: "category",
        data: sorted.map(i => i.dist_name),
        axisLabel: { color: C.text1, fontSize: 12, fontWeight: "600", padding: [0, 10, 0, 0] },
        axisLine: { lineStyle: { color: C.border, width: 1 } },
      },
      series: [{
        type: "bar",
        data: sorted.map(item => ({
          value: item.count,
          itemStyle: {
            color: { type: "linear", x: 0, y: 0, x2: 1, y2: 0,
              colorStops: [{ offset: 0, color: "#A0C8E0" }, { offset: 1, color: C.primary }],
            },
            borderRadius: [0, 10, 10, 0],
          },
        })),
        barWidth: 15,
        barMaxWidth: 20,
        barCategoryGap: "20%",
        label: { show: true, position: "right", formatter: p => p.value.toLocaleString("en-IN"), fontSize: 11, fontWeight: "700", color: C.primary },
        showBackground: true, backgroundStyle: { color: "rgba(42,101,145,.04)", borderRadius: [0, 10, 10, 0] },
      }],
      dataZoom: sorted.length > 6 ? [{
        type: "slider", yAxisIndex: 0, start: 0, end: 60, orient: "vertical",
        handleStyle: { color: C.primary }, borderColor: C.border, backgroundColor: C.surface,
      }] : [],
    };

    if (chartInstance.current) chartInstance.current.dispose();
    chartInstance.current = echarts.init(chartRef.current);
    chartInstance.current.setOption(option);
    chartInstance.current.on("click", p => {
      if (p.componentType === "series") {
        const d = chartData.find(x => x.dist_name === p.name);
        if (d && onBarClick) onBarClick(d);
      }
    });
    const onResize = () => chartInstance.current?.resize();
    window.addEventListener("resize", onResize);
    return () => { window.removeEventListener("resize", onResize); chartInstance.current?.dispose(); };
  }, [chartData, selectedIncident, isTrendExpanded]);

  if (!selectedIncident) return null;
  if (loading) return (
    <div style={{ textAlign: "center", padding: 40 }}>
      <Loader show={true} type="body" stack="vertical" message="Analyzing data..." />
    </div>
  );
  if (!chartData.length || !chartData.some(i => i.count > 0)) return (
    <div style={{ textAlign: "center", padding: 40, color: C.text3, fontSize: 13 }}>
      No data available for the selected period
    </div>
  );

  return <div ref={chartRef} style={{ width: "100%", height: dynamicHeight }} />;
}

// ── Pie Chart ───────────────────────────────────────────────────────────
function HeatwavePieChart({ cardData }) {
  const chartRef = useRef(null);
  const chartInstance = useRef(null);

  useEffect(() => {
    if (!chartRef.current) return;
    const keys = dashboardCardConfigForHeatwave.data;
    const blueShades = ["#1C4E72", "#2A6591", "#3A85B5", "#5AA0C0"];
    const seriesData = keys.map((cfg, idx) => ({
      name: formatLabel(cfg.key),
      value: cardData[cfg.key] || 0,
      itemStyle: { color: blueShades[idx] },
    })).filter(d => d.value > 0);

    const option = {
      title: { text: "IMPACT DISTRIBUTION", left: "center", top: 16, textStyle: { color: C.text1, fontSize: 12, fontWeight: "700", fontFamily: "'Sora',sans-serif" } },
      tooltip: {
        trigger: "item",
        formatter: p => `<div style="padding:4px 2px"><div style="font-weight:700;color:${C.text1};margin-bottom:4px">${p.name}</div><div style="color:${C.primary};font-weight:800;font-size:18px">${p.value.toLocaleString("en-IN")}</div><div style="color:${C.text3};font-size:11px">${p.percent.toFixed(1)}% of total</div></div>`,
        backgroundColor: C.surface, borderColor: C.border, borderWidth: 1,
        extraCssText: "border-radius:12px;box-shadow:0 4px 16px rgba(15,45,66,.1);",
      },
      legend: { orient: "horizontal", bottom: 12, itemWidth: 10, itemHeight: 10, itemGap: 14, textStyle: { color: C.text3, fontSize: 10, fontWeight: "600" } },
      series: [{
        type: "pie", radius: "60%", center: ["50%", "48%"],
        label: { show: false },
        emphasis: { itemStyle: { shadowBlur: 10, shadowOffsetX: 0, shadowColor: 'rgba(0, 0, 0, 0.5)' } },
        data: seriesData,
      }],
    };

    if (chartInstance.current) chartInstance.current.dispose();
    chartInstance.current = echarts.init(chartRef.current);
    chartInstance.current.setOption(option);
    const onResize = () => chartInstance.current?.resize();
    window.addEventListener("resize", onResize);
    return () => { window.removeEventListener("resize", onResize); chartInstance.current?.dispose(); };
  }, [cardData]);

  return (
    <div className="hwd-chart-cell">
      <div ref={chartRef} style={{ width: "100%", height: "100%" }} />
    </div>
  );
}

// ── Line Chart ───────────────────────────────────────────────────────────
function HeatwaveLineChart({ cardData }) {
  const chartRef = useRef(null);
  const chartInstance = useRef(null);

  useEffect(() => {
    if (!chartRef.current) return;
    const keys = dashboardCardConfigForHeatwave.data;
    
    const option = {
      title: { text: "CATEGORY TREND", left: "center", top: 16, textStyle: { color: C.text1, fontSize: 12, fontWeight: "700", fontFamily: "'Sora',sans-serif" } },
      tooltip: {
        trigger: "axis",
        backgroundColor: C.surface, borderColor: C.border, borderWidth: 1,
        extraCssText: "border-radius:12px;box-shadow:0 4px 16px rgba(15,45,66,.1);",
      },
      grid: { left: "10%", right: "10%", bottom: "15%", top: "25%", containLabel: true },
      xAxis: {
        type: "category",
        boundaryGap: false,
        data: keys.map(cfg => formatLabel(cfg.key)),
        axisLabel: { color: C.text3, fontSize: 9, rotate: 15 },
        axisLine: { lineStyle: { color: C.border } }
      },
      yAxis: {
        type: "value",
        axisLabel: { color: C.text3, fontSize: 10 },
        splitLine: { lineStyle: { color: C.border, type: "dashed" } }
      },
      series: [{
        data: keys.map(cfg => cardData[cfg.key] || 0),
        type: "line",
        smooth: true,
        symbolSize: 8,
        itemStyle: { color: C.primary },
        lineStyle: { width: 3, color: C.primary },
        areaStyle: {
          color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
            { offset: 0, color: "rgba(42,101,145,0.3)" },
            { offset: 1, color: "rgba(42,101,145,0)" }
          ])
        }
      }]
    };

    if (chartInstance.current) chartInstance.current.dispose();
    chartInstance.current = echarts.init(chartRef.current);
    chartInstance.current.setOption(option);
    const onResize = () => chartInstance.current?.resize();
    window.addEventListener("resize", onResize);
    return () => { window.removeEventListener("resize", onResize); chartInstance.current?.dispose(); };
  }, [cardData]);

  return (
    <div className="hwd-chart-cell">
      <div ref={chartRef} style={{ width: "100%", height: "100%" }} />
    </div>
  );
}

// ── Radar Chart ──────────────────────────────────────────────────────────
// ── Funnel Chart ────────────────────────────────────────────────────────
function HeatwaveFunnelChart({ cardData }) {
  const chartRef = useRef(null);
  const chartInstance = useRef(null);

  useEffect(() => {
    if (!chartRef.current) return;
    const keys = dashboardCardConfigForHeatwave.data;
    const blueShades = ["#1C4E72", "#2A6591", "#3A85B5", "#5AA0C0"];
    const seriesData = keys.map((cfg, idx) => ({
      name: formatLabel(cfg.key),
      value: cardData[cfg.key] || 0,
      itemStyle: { color: blueShades[idx] }
    })).sort((a,b) => b.value - a.value);

    const option = {
      title: { 
        text: "IMPACT ANALYSIS", 
        left: "center", top: 16, 
        textStyle: { color: C.text1, fontSize: 12, fontWeight: "700", fontFamily: "'Sora',sans-serif" } 
      },
      tooltip: {
        trigger: "item",
        formatter: p => `<div style="padding:4px 2px"><div style="font-weight:700;color:${C.text1};margin-bottom:4px">${p.name}</div><div style="color:${C.primary};font-weight:800;font-size:18px">${p.value.toLocaleString("en-IN")}</div></div>`,
        backgroundColor: C.surface, borderColor: C.border, borderWidth: 1,
        extraCssText: "border-radius:12px;box-shadow:0 4px 16px rgba(15,45,66,.1);",
      },
      series: [
        {
          name: "Impact Funnel",
          type: "funnel",
          left: "15%", top: 60, bottom: 40, width: "70%",
          min: 0,
          minSize: "0%",
          maxSize: "100%",
          sort: "descending",
          gap: 2,
          label: {
            show: true,
            position: "inside",
            fontSize: 10,
            fontWeight: "700",
            color: "#fff",
            formatter: "{b}"
          },
          labelLine: { show: false },
          itemStyle: { borderColor: "#fff", borderWidth: 1 },
          emphasis: { label: { fontSize: 12 } },
          data: seriesData
        }
      ]
    };

    if (chartInstance.current) chartInstance.current.dispose();
    chartInstance.current = echarts.init(chartRef.current);
    chartInstance.current.setOption(option);
    const onResize = () => chartInstance.current?.resize();
    window.addEventListener("resize", onResize);
    return () => { window.removeEventListener("resize", onResize); chartInstance.current?.dispose(); };
  }, [cardData]);

  return (
    <div className="hwd-chart-cell">
      <div ref={chartRef} style={{ width: "100%", height: "100%" }} />
    </div>
  );
}